/**
 * Loads game catalog data and client bundles at runtime via the worker's
 * game catalog (GameCatalogDO), instead of the static workspace-package
 * imports gameRegistry.ts uses for most games. Fetches the current
 * snapshot for the channel this session follows, then keeps a WebSocket
 * open so that when a new version is published to that channel, the
 * relevant game's version signal updates live — GameContainer uses that to
 * trigger a remount with the new code.
 */
import { signal, type Signal } from '@preact/signals-react'
import type {
  DynamicGameCatalogEntry,
  DynamicGameLoader,
  LoadedDynamicGame,
} from './dynamicGameLoader.types'

/** Replaces catalog contents while preserving metadata and the snapshot's authoritative order. */
export function replaceCatalogMap(
  catalog: Map<string, DynamicGameCatalogEntry>,
  entries: DynamicGameCatalogEntry[],
): string[] {
  const nextIds = new Set(entries.map((entry) => entry.gameId))
  const removedIds = [...catalog.keys()].filter((gameId) => !nextIds.has(gameId))
  const orderedEntries = entries.map((entry) => {
    const existing = catalog.get(entry.gameId)
    return existing ? { ...existing, ...entry } : entry
  })

  catalog.clear()
  for (const entry of orderedEntries) catalog.set(entry.gameId, entry)
  return removedIds
}

function toWebSocketUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

// The fallback channel this browser session follows for non-room dynamic queries.
const channel = 'stable'

// A dynamic import() that fails for a given URL can have that exact
// specifier permanently cached as a rejection in the browser's module map —
// retrying the identical URL string keeps failing even once the resource is
// actually fetchable, so each attempt below uses a fresh cache-busted URL.
// This matters here because the catalog push can arrive (and trigger an
// import attempt) right as the publish pipeline finishes writing the file —
// in dev, Vite's public/ file serving can take a moment (observed up to a
// couple of seconds) to notice a brand-new file, so a single immediate
// retry isn't always enough. Production static hosting has no such
// mid-request-serving delay, so this backoff is a dev-only safety net.
const RETRY_DELAYS_MS = [300, 600, 1200, 2000]

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function importModuleWithRetry(url: string): Promise<any> {
  try {
    return await import(/* @vite-ignore */ url)
  } catch (error) {
    console.warn(`[dynamicGameLoader] Initial import of ${url} failed, retrying:`, error)
  }

  for (const [i, ms] of RETRY_DELAYS_MS.entries()) {
    await delay(ms)
    const retryUrl = `${url}${url.includes('?') ? '&' : '?'}r=${Date.now()}`
    try {
      return await import(/* @vite-ignore */ retryUrl)
    } catch (error) {
      if (i === RETRY_DELAYS_MS.length - 1) throw error
      console.warn(`[dynamicGameLoader] Retry of ${url} failed, trying again:`, error)
    }
  }
}

// Unlike a failed dynamic import(), a <link rel="stylesheet"> hitting the
// same dev-server timing race fails *silently*: the link's `load` event can
// still fire with an attached-but-empty CSSStyleSheet (0 rules) rather than
// an `error` event, and a <link> never auto-retries. So this checks
// cssRules.length explicitly rather than trusting onload alone.
function attachStylesheet(url: string): Promise<HTMLLinkElement> {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    // Without this, a cross-origin stylesheet (e.g. served from R2) loads
    // and applies fine, but reading link.sheet.cssRules below throws
    // SecurityError regardless of the server's CORS headers — the browser
    // only exposes CSSOM for a cross-origin resource fetched in CORS mode.
    link.crossOrigin = 'anonymous'
    link.href = url
    link.onload = () => {
      const ruleCount = link.sheet?.cssRules?.length ?? 0
      if (ruleCount > 0) {
        resolve(link)
      } else {
        link.remove()
        reject(new Error(`Stylesheet ${url} loaded with no rules`))
      }
    }
    link.onerror = () => {
      link.remove()
      reject(new Error(`Failed to load stylesheet ${url}`))
    }
    document.head.appendChild(link)
  })
}

async function attachStylesheetWithRetry(url: string): Promise<HTMLLinkElement> {
  try {
    return await attachStylesheet(url)
  } catch (error) {
    console.warn(`[dynamicGameLoader] Initial stylesheet load of ${url} failed, retrying:`, error)
  }

  for (const [i, ms] of RETRY_DELAYS_MS.entries()) {
    await delay(ms)
    const retryUrl = `${url}${url.includes('?') ? '&' : '?'}r=${Date.now()}`
    try {
      return await attachStylesheet(retryUrl)
    } catch (error) {
      if (i === RETRY_DELAYS_MS.length - 1) throw error
      console.warn(`[dynamicGameLoader] Stylesheet retry of ${url} failed, trying again:`, error)
    }
  }
  throw new Error(`Failed to load stylesheet ${url} after retries`)
}

// Loads the new stylesheet fully (with retry) before touching the old one,
// so a game never has a moment with no styles at all — better a stale
// stylesheet stays visible during the swap than a flash of unstyled content.
async function setStylesheet(injected: Map<string, HTMLLinkElement>, gameId: string, url: string | undefined): Promise<void> {
  const existing = injected.get(gameId)
  if (existing && existing.href === url) return
  if (!url) {
    existing?.remove()
    injected.delete(gameId)
    return
  }
  const link = await attachStylesheetWithRetry(url)
  existing?.remove()
  injected.set(gameId, link)
}

export function makeDynamicGameLoader(
  workerUrl: string,
  importModule: (url: string) => Promise<any> = importModuleWithRetry,
  getAuthToken?: () => Promise<string | null>,
): DynamicGameLoader {
  const catalog = new Map<string, DynamicGameCatalogEntry>()
  // Entries looked up for a room (see lookupCatalogEntries); not browsable.
  const sharedEntries = new Map<string, DynamicGameCatalogEntry>()
  const catalogEntries = signal<DynamicGameCatalogEntry[]>([])
  const resolvedModules = new Map<string, LoadedDynamicGame>()
  const versionSignals = new Map<string, Signal<string | null>>()
  const entrySignals = new Map<string, Signal<DynamicGameCatalogEntry | null>>()
  const injectedStylesheets = new Map<string, HTMLLinkElement>()
  const moduleCache = new Map<string, Promise<LoadedDynamicGame>>()

  function getVersionSignal(gameId: string): Signal<string | null> {
    let sig = versionSignals.get(gameId)
    if (!sig) {
      sig = signal<string | null>(null)
      versionSignals.set(gameId, sig)
    }
    return sig
  }

  function getEntrySignal(gameId: string): Signal<DynamicGameCatalogEntry | null> {
    let sig = entrySignals.get(gameId)
    if (!sig) {
      sig = signal<DynamicGameCatalogEntry | null>(null)
      entrySignals.set(gameId, sig)
    }
    return sig
  }

  function publishEntry(entry: DynamicGameCatalogEntry): void {
    getVersionSignal(entry.gameId).value = entry.clientBundleUrl
    getEntrySignal(entry.gameId).value = entry
  }

  // A game known only from a room's lookup stays out of the browsable catalog.
  function applyEntry(entry: DynamicGameCatalogEntry): void {
    const store = !catalog.has(entry.gameId) && sharedEntries.has(entry.gameId) ? sharedEntries : catalog
    const existing = store.get(entry.gameId)
    const merged = existing ? { ...existing, ...entry } : entry
    store.set(entry.gameId, merged)
    publishEntry(merged)
    if (store === catalog) catalogEntries.value = [...catalog.values()]
  }

  function replaceCatalog(entries: DynamicGameCatalogEntry[]): void {
    const removedIds = replaceCatalogMap(catalog, entries)
    for (const gameId of removedIds) {
      getVersionSignal(gameId).value = null
      getEntrySignal(gameId).value = null
    }
    for (const merged of catalog.values()) publishEntry(merged)
    catalogEntries.value = [...catalog.values()]
  }

  let socket: WebSocket | null = null
  function ensureConnected(): void {
    if (socket || typeof window === 'undefined') return
    socket = new WebSocket(`${toWebSocketUrl(workerUrl)}/api/games/websocket?channel=${encodeURIComponent(channel)}`)

    socket.addEventListener('message', (event) => {
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'catalogSnapshot') {
          for (const entry of msg.versions as DynamicGameCatalogEntry[]) {
            applyEntry(entry)
          }
        } else if (msg.type === 'versionPublished') {
          applyEntry(msg.version as DynamicGameCatalogEntry)
        }
      } catch (error) {
        console.error('[dynamicGameLoader] Failed to handle catalog message:', error)
      }
    })

    socket.addEventListener('close', () => {
      socket = null
      // Simple fixed-delay reconnect; fine for a pilot. Real backoff can
      // come later if this proves flaky.
      setTimeout(ensureConnected, 2000)
    })

    socket.addEventListener('error', () => {
      socket?.close()
    })
  }

  const refreshCatalog = async (): Promise<DynamicGameCatalogEntry[]> => {
    const token = await getAuthToken?.()
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined
    const res = await fetch(`${workerUrl}/api/games/catalog?scope=visible`, { headers })
    if (!res.ok) {
      throw new Error(`Failed to fetch game catalog: ${res.status}`)
    }
    const data = (await res.json()) as { versions: DynamicGameCatalogEntry[] }
    replaceCatalog(data.versions)
    return [...catalog.values()]
  }

  const getCatalog = async (): Promise<DynamicGameCatalogEntry[]> => {
    ensureConnected()
    if (catalog.size === 0) await refreshCatalog()
    return [...catalog.values()]
  }

  // Shared by loadGameClient (channel lookup) and loadGameClientAtVersion
  // (explicit, room-pinned/pushed version — see GameContainer) — same
  // import/stylesheet mechanics either way, just a different source for
  // which URL to load.
  const loadModule = async (
    gameId: string,
    bundleInfo: { clientBundleUrl: string; styleUrl?: string },
  ): Promise<LoadedDynamicGame> => {
    // Headless Node clients have no DOM to attach a stylesheet to, and load
    // game logic only — never the styling.
    const stylesheetReady = typeof document === 'undefined'
      ? Promise.resolve()
      : setStylesheet(injectedStylesheets, gameId, bundleInfo.styleUrl)

    const cached = moduleCache.get(bundleInfo.clientBundleUrl)
    if (cached) {
      await stylesheetReady
      return cached
    }

    // Vite's dev server refuses to serve public/ files through its module
    // transform pipeline for root-relative import() specifiers — a
    // fully-qualified absolute URL routes around that dev-only
    // interception (no such middleware exists in production). A worker's
    // clientBundleUrl is already absolute, so a headless Node client (no
    // `window`) can use it as-is.
    const absoluteUrl = typeof window === 'undefined'
      ? bundleInfo.clientBundleUrl
      : new URL(bundleInfo.clientBundleUrl, window.location.origin).href
    const promise = importModule(absoluteUrl).then((mod) => {
      if (typeof mod.Game !== 'function') {
        throw new Error(`Dynamically loaded module for "${gameId}" has no Game export`)
      }
      if (!mod.gameLogic || mod.gameLogic.framework !== 'bgio' || !mod.gameLogic.bgioGame) {
        throw new Error(`Dynamically loaded module for "${gameId}" has no supported gameLogic export`)
      }
      const loaded = {
        Game: mod.Game,
        Rulebook: typeof mod.Rulebook === 'function' ? mod.Rulebook : undefined,
        PlayerPiece: typeof mod.PlayerPiece === 'function' ? mod.PlayerPiece : undefined,
        themeConfig: mod.themeConfig,
        gameLogic: mod.gameLogic,
        options: mod.options || [],
        playerIds: mod.playerIds,
        positions: mod.positions || mod.testStates,
        testStates: mod.positions || mod.testStates,
        createBot: mod.createBot,
      }
      resolvedModules.set(gameId, loaded)
      return loaded
    })
    // Don't leave a fully-failed load cached — a later call (e.g. after a
    // transient network issue clears) should get a fresh attempt.
    promise.catch(() => moduleCache.delete(bundleInfo.clientBundleUrl))
    moduleCache.set(bundleInfo.clientBundleUrl, promise)

    // Wait for the stylesheet too so the caller only ever sees "fully ready"
    // or "still loading" — never JS-ready-but-unstyled.
    await stylesheetReady
    return promise
  }

  const loadGameClient = async (gameId: string): Promise<LoadedDynamicGame> => {
    await getCatalog()
    const entry = catalog.get(gameId) ?? sharedEntries.get(gameId)
    if (!entry) {
      throw new Error(`No catalog entry for game "${gameId}" on channel "${channel}"`)
    }
    return loadModule(gameId, entry)
  }

  // Records the room-pinned/pushed version into the same entry/version
  // signals the channel-follow path (applyEntry, above) populates, so
  // debug/inspection UI reflects reality regardless of which path loaded
  // the currently-active build.
  const loadGameClientAtVersion = (
    gameId: string,
    version: { versionId: string; clientBundleUrl: string; styleUrl?: string },
  ): Promise<LoadedDynamicGame> => {
    applyEntry({ gameId, versionId: version.versionId, clientBundleUrl: version.clientBundleUrl, styleUrl: version.styleUrl })
    return loadModule(gameId, version)
  }

  // Independent of the session's followed `channel` — used to pin a specific
  // channel's (usually "stable") version at game start, not to live-follow.
  const resolveChannelVersion = async (gameId: string, resolveChannel: string) => {
    const res = await fetch(`${workerUrl}/api/games/catalog?channel=${encodeURIComponent(resolveChannel)}`)
    if (!res.ok) return null
    const data = (await res.json()) as { versions: DynamicGameCatalogEntry[] }
    const entry = data.versions.find((v) => v.gameId === gameId)
    return entry ?? null
  }

  const lookupCatalogEntries = async (gameIds: string[]): Promise<DynamicGameCatalogEntry[]> => {
    if (gameIds.length === 0) return []
    const ids = gameIds.map(encodeURIComponent).join(',')
    const res = await fetch(`${workerUrl}/api/games/catalog?scope=games&ids=${ids}`)
    if (!res.ok) return []
    const data = (await res.json()) as { versions: DynamicGameCatalogEntry[] }
    for (const entry of data.versions) {
      if (catalog.has(entry.gameId)) continue
      sharedEntries.set(entry.gameId, entry)
      publishEntry(entry)
    }
    return data.versions
  }

  const listVersions = async (gameId: string): Promise<DynamicGameCatalogEntry[]> => {
    const res = await fetch(`${workerUrl}/api/games/versions/${encodeURIComponent(gameId)}`)
    if (!res.ok) return []
    const data = (await res.json()) as { versions: DynamicGameCatalogEntry[] }
    return data.versions
  }

  return {
    catalogEntries,
    getResolvedModule: (gameId: string) => resolvedModules.get(gameId),
    getCatalog,
    refreshCatalog,
    loadGameClient,
    loadGameClientAtVersion,
    getCurrentVersionSignal: getVersionSignal,
    getCurrentEntrySignal: getEntrySignal,
    getChannel: () => channel,
    resolveChannelVersion,
    listVersions,
    lookupCatalogEntries,
  }
}
